/**
 * Sanitize an Input to remove any special characters that might cause XSS injection
 * @param str
 * @return {string}
 */
export function sanitizeInput(str: string): string {
	// if input is null or undefined, just return
	if (!str) {
		return str
	}

	return str
		.replaceAll('&', '&amp;')
		.replaceAll('>', '&gt;')
		.replaceAll('<', '&lt;')
		.replaceAll('"', '&#34;')
		.replaceAll("'", '&#39;')
}

/**
 * Sanitize an image URL taken from the data-colors attribute.
 * Only allows http/https absolute URLs or same-origin relative URLs.
 */
export function sanitizeUrl(url: string): string {
	if (!url) {
		return ''
	}

	// Reject URLs containing whitespace or control characters
	// eslint-disable-next-line no-control-regex
	if (/[\s\x00-\x1F\x7F]/.test(url)) {
		console.warn(`Blocked image URL with invalid characters "${url}"`)
		return ''
	}

	try {
		const base = window.location.origin
		const parsed = new URL(url, base)

		// Only allow http/https schemes
		if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
			console.warn(`Blocked image URL with unsupported protocol "${url}"`)
			return ''
		}

		// If the original URL was relative, ensure it stays on the same origin
		const isAbsoluteInput = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url)
		if (!isAbsoluteInput && parsed.origin !== base) {
			console.warn(`Blocked cross-origin relative image URL "${url}"`)
			return ''
		}

		return parsed.toString()
	} catch (e) {
		console.warn('Invalid image URL', e)
	}

	console.warn(`Blocked potentially unsafe image URL "${url}"`)
	return ''
}

/**
 * Use DOMPurify to sanitize HTML input; Fallback to sanitizeInput if DOMPurify is not available.
 */
export function sanitizeHtml(html: string): string {
	return window.gmxDOMPurify?.sanitize
		? window.gmxDOMPurify.sanitize(html)
		: sanitizeInput(html)
}
